import React, { useState } from 'react';
import { makeStyles, Typography, Tooltip } from '@material-ui/core';
import '../../App.css';
import PlaySourcesTable from './PlaySourcesTable';
import { BrandSearchInput } from '../CoreComponents/BrandInput';
import { useDebounce, DEBOUNCE_DELAY_MILLISECONDS } from '../../hooks/useDebounce';
import { BrandCheckbox } from '../CoreComponents/BrandCheckbox';
import { BrandButton, BrandDeleteButton } from '../CoreComponents/BrandButton';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import CloseOutlinedIcon from '@material-ui/icons/CloseOutlined';
import { BrandAutocomplete } from '../CoreComponents/BrandAutoComplete';
import { useEffect } from 'react';
import useAbortableFetch from '../../hooks/useAbortableFetch';
import DoneIcon from '@material-ui/icons/Done';
import CloseIcon from '@material-ui/icons/Close';
import { BrandTooltip } from '../CoreComponents/BrandTooltip';
import { useSnackbarAlert } from '../../hooks/useSnackbarAlert';

const useStyles = makeStyles((theme) => ({
  consumptionData: {
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box'
  },
  heading: {
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    marginBottom: theme.spacing(2)
  },
  subHeading: {
    color: theme.palette.text.primary
  },
  consumptionDataContainer: {
    width: '100%'
  },
  filtersWrapper: {
    paddingBottom: theme.spacing(3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    columnGap: theme.spacing(3),
    width: '100%'
  },
  searchInput: {
    width: theme.spacing(57),
  },
  checkboxContainer: {
    marginLeft: 'auto'
  },
  multiEditButton: {
    marginLeft: 'auto',
    minWidth: '190px',
    display: 'flex',
    justifyContent: 'flex-end'
  },
  multiEditEnabled: {
    marginLeft: 'auto',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    columnGap: theme.spacing(3),
  },
  autocompleteContainer: {
    width: '100%',
    minWidth: '410px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    columnGap: theme.spacing(1)
  },
  saveIcon: {
    color: theme.palette.success.main,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  cancelIcon: {
    color: theme.palette.error.main,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
}));

const ConsumptionData = () => {
  const styles = useStyles();
  const abortableFetch = useAbortableFetch();
  const { showSuccessAlert, showErrorAlert } = useSnackbarAlert();
  const [playSourceSearch, setPlaySourceSearch] = useState('');
  const playSourceSearchQuery = useDebounce(playSourceSearch, DEBOUNCE_DELAY_MILLISECONDS);
  const [showNoDisplayTitleSources, setShowNoDisplayTitleSources] = useState(true);
  const [multiEditEnabled, setMultiEditEnabled] = useState(false);
  const multiEditTooltipTitle = "In Multi Edit mode you can rename multiple display titles at the same time";
  const [selectedPlaySource, setSelectedPlaySource] = useState(null);
  const [playSources, setPlaySources] = useState([]);
  const [checkedItems, setCheckedItems] = useState(new Set());
  const [items, setItems] = useState([]);

  useEffect(() => {
    fetchPlaySources();
  }, []);

  const fetchPlaySources = async () => {
    try {
      const playSources = await abortableFetch('GET', '/api/consumption-data/play-sources');
      setPlaySources(playSources);
    } catch (error) {
      console.error(error);
    }
  }

  const enableMultiEdit = () => {
    setMultiEditEnabled(true);
  }

  const disableMultiEdit = () => {
    setMultiEditEnabled(false);
    setCheckedItems(new Set());
  }

  const handleSave = async () => {
    try {

      if(!selectedPlaySource) {
        showErrorAlert('Please select a play source to update');
        return;
      }

      let newDisplayTitle;

      if(selectedPlaySource?.isNew || selectedPlaySource?.name) {
        newDisplayTitle = selectedPlaySource.name;
      }
      else {
        newDisplayTitle = playSources.find(source => source.id === selectedPlaySource)?.name;
      }

      const response = await abortableFetch('PUT', '/api/consumption-data/', {
        body: {
          ids: Array.from(checkedItems),
          displayTitle: newDisplayTitle
        }
      });
      showSuccessAlert('Display title updated to all selected play sources successfully');
      
      setItems(prevItems => prevItems.map(item =>
        checkedItems.has(item.id)
          ? { ...item, displayTitle: newDisplayTitle }
          : item
      ));
      
      setCheckedItems(new Set());
    } catch (error) {
      console.error(error);
      showErrorAlert('Error updating display title to the selected play sources');
    }
  }

  const handleCancel = () => {
    setMultiEditEnabled(false);
    setCheckedItems(new Set());
  }

  return (
    <div className={styles.consumptionDataContainer}>
      <div>
        <Typography variant="h6" className={styles.heading} sx={{ paddingBottom: 20 }}>
          Consumption Data: <span className={styles.subHeading}>Play Sources</span>
        </Typography>
      </div>

      <div className={styles.filtersWrapper}>
        <BrandSearchInput
          placeholder={`Search by Original or Display Title`}
          value={playSourceSearch}
          onChange={(event) => setPlaySourceSearch(event.target.value)}
          classes={{ input: styles.searchInput }}
        />
        <BrandCheckbox
          label="Hide play sources with display title"
          checked={showNoDisplayTitleSources}
          onChange={(event) => setShowNoDisplayTitleSources(event.target.checked)}
        />
        {multiEditEnabled ? (
          <div className={styles.multiEditEnabled}>
            <div className={styles.autocompleteContainer}>
              <BrandAutocomplete
                placeholder={'Type your display title here'}
                autoCompleteOptions={playSources}
                selectedAutoCompleteOption={selectedPlaySource}
                onSelectedAutoCompleteOption={setSelectedPlaySource}
                allowNewOptions={true}
              />
              <div className={styles.saveIcon}>
                <DoneIcon fontSize="medium" onClick={handleSave}/>
              </div>
              <div className={styles.cancelIcon}>
                <CloseIcon fontSize="medium" onClick={handleCancel}/>
              </div>
            </div>
            <div className={styles.multiEditButton}>
              <BrandDeleteButton
                startIcon={<CloseOutlinedIcon />}
                onClick={disableMultiEdit}
              >Exit Multi Edit
              </BrandDeleteButton>
            </div>
          </div>
        ) : (
          <div className={styles.multiEditButton}>
            <BrandTooltip title={multiEditTooltipTitle}>
              <BrandButton
                startIcon={<EditOutlinedIcon />}
                onClick={enableMultiEdit}
              >Multi Edit</BrandButton>
            </BrandTooltip>
          </div>)}
      </div>

      <PlaySourcesTable 
        playSourceSearch={playSourceSearchQuery}
        showNoDisplayTitleSources={showNoDisplayTitleSources}
        multiEditEnabled={multiEditEnabled}
        checkedItems={checkedItems}
        setCheckedItems={setCheckedItems}
        setItems={setItems}
        items={items}
      />
    </div>
  );
};

export default ConsumptionData;
